// Mehrere Stundenzettel auf einmal (server/src/modules/rechnungstool/stundenzettel-stapel.ts): Grenzen, Typen der
// Antwort und Lesen der Antwort Zeile für Zeile (Fortschritt), Fotos vorher verkleinern.
import { ApiError, tarifFehlerMelden } from '../api';
import type { StundenZeile } from './stundenPositionen';
import type { StundenEmpfaenger } from './types';

// wie auf dem Server
export const STAPEL = { dateien: 30, seiten: 60, dateiMb: 20, zusammenMb: 80 };

export type ZuordnungStand = 'eindeutig' | 'pruefen' | 'nicht_gefunden' | 'fehlt';

export type StapelZettel = { nr: number; datei: number; name: string; seiten: [number, number] | null } & (
  | {
      ok: true;
      empfaenger: StundenEmpfaenger | null;
      objekt: string | null;
      eintraege: StundenZeile[];
      hinweis: string | null;
      stunden: number;
    }
  | { ok: false; fehler: string }
);
export type GelesenerZettel = Extract<StapelZettel, { ok: true }>;

// Eine Rechnung: Zettel zum selben Objekt (und Empfänger), Kunde von der App vorgeschlagen
export interface StapelGruppe {
  key: string;
  objekt: string | null;
  empfaenger: StundenEmpfaenger | null;
  zuordnung: { kundeId: string | null; stand: ZuordnungStand; grund: string | null };
  zettel: number[];
  von: string | null;
  bis: string | null;
  stunden: number;
}

export type StapelEreignis =
  | { t: 'start'; dateien: number; seiten: number; zettel: number }
  | { t: 'geteilt'; datei: number; zettel: number }
  | { t: 'gesamt'; zettel: number }
  | ({ t: 'zettel' } & StapelZettel)
  | { t: 'ende'; zettel: number; gelesen: number; fehler: number; gruppen: StapelGruppe[] }
  | { t: 'fehler'; fehler: string }
  | { t: 'warte' };

// Antwort lesen, sobald sie kommt: je Zeile ein Ereignis
export async function stapelLesen(url: string, files: File[], on: (e: StapelEreignis) => void, signal: AbortSignal): Promise<void> {
  const fd = new FormData();
  for (const f of files) fd.append('dateien', f, f.name);
  let res: Response;
  try {
    res = await fetch(url, { method: 'POST', body: fd, credentials: 'same-origin', signal });
  } catch (err) {
    if (signal.aborted) throw err;
    throw new ApiError(0, 'Keine Verbindung. Bitte prüfen Sie Ihr Internet und versuchen Sie es noch einmal.');
  }
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string; code?: string } & Record<string, unknown>;
    if (res.status === 401) window.dispatchEvent(new CustomEvent('nt:logged-out', { detail: data.code }));
    if (res.status === 413 && !data.error) throw new ApiError(413, 'Die Dateien sind zusammen zu groß.', 'too_large');
    tarifFehlerMelden(res.status, data);
    throw new ApiError(res.status, data.error ?? 'Da ist etwas schiefgelaufen.', data.code, data);
  }
  if (!res.body) throw new ApiError(0, 'Da ist etwas schiefgelaufen.');
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  const zeile = (l: string) => {
    if (l.trim()) on(JSON.parse(l) as StapelEreignis);
  };
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i: number;
    while ((i = buf.indexOf('\n')) >= 0) {
      zeile(buf.slice(0, i));
      buf = buf.slice(i + 1);
    }
  }
  zeile(buf + dec.decode());
}

// Große Fotos (Handy: oft 3–8 MB) vorher verkleinern: Die KI braucht nicht mehr als etwa 2.500 Pixel, das Hochladen
// geht schneller und viele Fotos passen in einen Durchgang. Dabei fallen auch Standort und Gerätedaten weg. Geht das
// nicht (alter Browser), bleibt das Foto, wie es ist.
const GROSS = 2.5 * 1024 * 1024;
const KANTE = 2600;
export async function verkleinern(f: File): Promise<File> {
  if (f.size <= GROSS || !/^image\/(jpeg|png|webp)$/.test(f.type) || typeof createImageBitmap !== 'function') return f;
  try {
    const bmp = await createImageBitmap(f);
    const k = Math.min(1, KANTE / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * k);
    canvas.height = Math.round(bmp.height * k);
    const ctx = canvas.getContext('2d');
    if (!ctx) return f;
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    bmp.close();
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.85));
    if (!blob || blob.size >= f.size) return f;
    return new File([blob], f.name.replace(/\.(png|webp|jpe?g)$/i, '') + '.jpg', { type: 'image/jpeg' });
  } catch {
    return f;
  }
}
